<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { Doughnut, Bar } from 'vue-chartjs';
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
} from 'chart.js';
import segueDashboardAPI from 'dashboard/api/segueDashboard';

const props = defineProps({
  period: { type: String, default: 'today' },
  customFrom: { type: String, default: null },
  customTo: { type: String, default: null },
});

ChartJS.register(
  Title,
  Tooltip,
  Legend,
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale
);

const REFRESH_MS = 5 * 60 * 1000;

const CATEGORY_COLORS = {
  Pagos: '#10b981',
  'Aguardando Pagamento': '#f59e0b',
  'Aguardando Assinatura': '#3b82f6',
  'Em Análise': '#94a3b8',
  Cancelados: '#ef4444',
  Reprovados: '#991b1b',
  Outros: '#475569',
};

const data = ref({
  bancos: { uy3: { hoje: [] }, facta: { hoje: [] }, c6: { hoje: [] } },
  pizza: { hoje: [], mes: [] },
  serie: { hoje: [], '7d': [], mes: [], ano: [] },
  categorias: [],
});
const isLoading = ref(false);
const error = ref(null);
const seriePeriod = ref('hoje');
const lastUpdate = ref(null);

// Per-bank custom date overrides. Null = follows global period.
const bankOverrides = ref({ uy3: null, facta: null, c6: null });

// Which card has the filter popover open right now (or null).
const filterOpenFor = ref(null);
const filterFrom = ref('');
const filterTo = ref('');

let timer = null;

const brl = v => {
  const n = Number(v) || 0;
  return n.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  });
};

const fetchData = async () => {
  isLoading.value = true;
  error.value = null;
  try {
    const overrides = {};
    Object.entries(bankOverrides.value).forEach(([bank, range]) => {
      if (range?.from && range?.to) overrides[bank] = range;
    });
    const r = await segueDashboardAPI.fetchBancosResumo({
      period: props.period,
      from: props.customFrom,
      to: props.customTo,
      bankOverrides: Object.keys(overrides).length ? overrides : null,
    });
    data.value = r.data;
    lastUpdate.value = new Date();
  } catch (e) {
    error.value = e.response?.data?.error || e.message || 'Erro ao carregar';
  } finally {
    isLoading.value = false;
  }
};

const lastUpdateLabel = computed(() => {
  if (!lastUpdate.value) return '';
  const fmt = new Intl.DateTimeFormat('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  return fmt.format(lastUpdate.value).replace(',', '');
});

const banks = computed(() => [
  { key: 'uy3', title: 'BANCO UY3' },
  { key: 'facta', title: 'BANCO FACTA' },
  { key: 'c6', title: 'BANCO C6' },
]);

const periodLabel = p => {
  if (p === 'today') return 'HOJE';
  if (p === 'yesterday') return 'ONTEM';
  if (p === '7d') return '7 DIAS';
  if (p === '30d') return '30 DIAS';
  if (p === 'custom') return 'CUSTOM';
  return p?.toUpperCase() || 'HOJE';
};

const formatBR = iso => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y.slice(2)}`;
};

const cardSubtitle = bankKey => {
  const override = bankOverrides.value[bankKey];
  if (override?.from && override?.to) {
    return `${formatBR(override.from)} – ${formatBR(override.to)}`;
  }
  return periodLabel(props.period);
};

const openFilter = bankKey => {
  filterOpenFor.value = bankKey;
  const cur = bankOverrides.value[bankKey];
  filterFrom.value = cur?.from || data.value.period?.from || '';
  filterTo.value = cur?.to || data.value.period?.to || '';
};

const closeFilter = () => {
  filterOpenFor.value = null;
};

const applyFilter = bankKey => {
  if (!filterFrom.value || !filterTo.value) return;
  bankOverrides.value = {
    ...bankOverrides.value,
    [bankKey]: { from: filterFrom.value, to: filterTo.value },
  };
  closeFilter();
  fetchData();
};

const clearFilter = bankKey => {
  bankOverrides.value = { ...bankOverrides.value, [bankKey]: null };
  closeFilter();
  fetchData();
};

const buildPizza = items => ({
  labels: items.map(i => i.categoria),
  datasets: [
    {
      data: items.map(i => i.qtd),
      backgroundColor: items.map(
        i => CATEGORY_COLORS[i.categoria] || '#475569'
      ),
      borderWidth: 0,
    },
  ],
});

const pizzaHoje = computed(() => buildPizza(data.value.pizza?.hoje || []));
const pizzaMes = computed(() => buildPizza(data.value.pizza?.mes || []));

const totalHoje = computed(() => {
  const items = data.value.pizza?.hoje || [];
  const qtd = items.reduce((s, i) => s + i.qtd, 0);
  const valor = items.reduce((s, i) => s + (i.valor || 0), 0);
  return `${qtd} propostas · ${brl(valor)}`;
});
const totalMes = computed(() => {
  const items = data.value.pizza?.mes || [];
  const qtd = items.reduce((s, i) => s + i.qtd, 0);
  const valor = items.reduce((s, i) => s + (i.valor || 0), 0);
  return `${qtd} propostas · ${brl(valor)}`;
});

const serieChart = computed(() => {
  const buckets = data.value.serie?.[seriePeriod.value] || [];
  const cats = data.value.categorias || [];
  if (seriePeriod.value === 'hoje') {
    return {
      labels: buckets.map(b => b.categoria),
      datasets: [
        {
          data: buckets.map(b => b.qtd),
          backgroundColor: buckets.map(
            b => CATEGORY_COLORS[b.categoria] || '#475569'
          ),
          borderWidth: 0,
        },
      ],
    };
  }
  const monthLabels = [
    'jan',
    'fev',
    'mar',
    'abr',
    'mai',
    'jun',
    'jul',
    'ago',
    'set',
    'out',
    'nov',
    'dez',
  ];
  const labels = buckets.map(b => {
    if (seriePeriod.value === 'ano') {
      const m = parseInt(b.label.split('-')[1], 10);
      return monthLabels[m - 1];
    }
    const parts = b.label.split('-');
    return `${parts[2]}/${parts[1]}`;
  });
  const used = cats.filter(c => buckets.some(b => b[c]?.qtd > 0));
  const datasets = used.map(cat => ({
    label: cat,
    data: buckets.map(b => b[cat]?.qtd || 0),
    backgroundColor: CATEGORY_COLORS[cat] || '#475569',
    borderWidth: 0,
  }));
  return { labels, datasets };
});

const buildPizzaOptions = sourceItems => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'bottom',
      labels: { color: '#cbd0e0', font: { size: 10 }, boxWidth: 10 },
    },
    tooltip: {
      callbacks: {
        label: ctx => {
          const orig = sourceItems.value[ctx.dataIndex];
          if (orig)
            return `${orig.categoria}: ${orig.qtd} · ${brl(orig.valor)}`;
          return `${ctx.label}: ${ctx.parsed}`;
        },
      },
    },
  },
});

const pizzaHojeOptions = computed(() =>
  buildPizzaOptions(computed(() => data.value.pizza?.hoje || []))
);
const pizzaMesOptions = computed(() =>
  buildPizzaOptions(computed(() => data.value.pizza?.mes || []))
);

const serieOptions = computed(() => {
  const isStacked = seriePeriod.value !== 'hoje';
  const buckets = data.value.serie?.[seriePeriod.value] || [];
  return {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: isStacked
        ? {
            position: 'bottom',
            labels: { color: '#cbd0e0', font: { size: 10 }, boxWidth: 10 },
          }
        : { display: false },
      tooltip: {
        callbacks: {
          label: ctx => {
            if (!isStacked) {
              const it = buckets[ctx.dataIndex];
              return it ? `${it.qtd} · ${brl(it.valor)}` : '';
            }
            const cat = ctx.dataset.label;
            const b = buckets[ctx.dataIndex];
            const e = (b && b[cat]) || { qtd: 0, valor: 0 };
            return `${cat}: ${e.qtd} · ${brl(e.valor)}`;
          },
        },
      },
    },
    scales: {
      x: {
        stacked: isStacked,
        ticks: { color: '#8f94a8', font: { size: 10 } },
        grid: { display: false },
      },
      y: {
        stacked: isStacked,
        ticks: { color: '#8f94a8' },
        grid: { color: '#2a2e4030' },
        beginAtZero: true,
      },
    },
  };
});

// When the global PeriodFilter changes: clear all per-bank overrides + refetch.
watch(
  () => [props.period, props.customFrom, props.customTo],
  () => {
    bankOverrides.value = { uy3: null, facta: null, c6: null };
    fetchData();
  }
);

onMounted(() => {
  fetchData();
  timer = setInterval(fetchData, REFRESH_MS);
});
onBeforeUnmount(() => {
  if (timer) clearInterval(timer);
});
</script>

<!-- eslint-disable vue/no-bare-strings-in-template -->
<template>
  <section class="flex flex-col gap-4">
    <div class="flex justify-between items-center">
      <div class="flex gap-3 items-baseline">
        <h2 class="text-lg font-semibold text-n-slate-12 tracking-wider">
          BANCOS
        </h2>
        <span v-if="lastUpdateLabel" class="text-xs text-n-slate-11">
          Última atualização: {{ lastUpdateLabel }}
        </span>
      </div>
      <button
        class="px-2 py-1 text-xs rounded border border-n-weak text-n-slate-11 hover:text-n-slate-12 hover:border-n-strong"
        :disabled="isLoading"
        @click="fetchData"
      >
        ↻ Atualizar
      </button>
    </div>

    <div
      v-if="error"
      class="p-3 text-sm rounded-xl border border-n-ruby-6 bg-n-ruby-3 text-n-ruby-11"
    >
      {{ error }}
    </div>

    <!-- 3 cards de banco -->
    <div class="grid grid-cols-1 gap-3 md:grid-cols-3">
      <div
        v-for="bank in banks"
        :key="bank.key"
        class="relative p-4 rounded-2xl border border-n-weak bg-n-background"
      >
        <div class="flex justify-between items-start gap-2 mb-2">
          <div class="flex flex-col">
            <div class="text-xs font-semibold tracking-wide text-n-blue-10">
              {{ bank.title }}
            </div>
            <div class="text-xs text-n-slate-11">
              {{ cardSubtitle(bank.key) }}
            </div>
          </div>
          <button
            class="px-2 py-0.5 text-[10px] rounded border border-n-weak text-n-slate-11 hover:text-n-slate-12 hover:border-n-strong"
            @click="openFilter(bank.key)"
          >
            Filtrar
          </button>
        </div>

        <ul
          v-if="data.bancos[bank.key]?.hoje?.length"
          class="flex flex-col gap-1 text-sm"
        >
          <li
            v-for="row in data.bancos[bank.key].hoje"
            :key="row.label"
            class="flex justify-between items-center gap-2"
          >
            <span class="flex gap-2 items-center">
              <span
                class="inline-block rounded-full size-2"
                :style="{
                  background: CATEGORY_COLORS[row.categoria] || '#475569',
                }"
              />
              <span class="text-n-slate-12">{{ row.label }}</span>
            </span>
            <span class="text-xs tabular-nums text-n-slate-11">
              <strong class="text-n-slate-12">{{ row.qtd }}</strong>
              · {{ brl(row.valor) }}
            </span>
          </li>
        </ul>
        <p v-else class="text-xs text-n-slate-11">Sem propostas no período</p>

        <!-- Popover de filtro por banco -->
        <div
          v-if="filterOpenFor === bank.key"
          class="absolute z-10 right-2 top-12 p-3 w-64 rounded-xl border border-n-weak bg-n-solid-1 shadow-lg flex flex-col gap-2"
        >
          <div class="text-xs font-semibold text-n-slate-12">
            Filtrar {{ bank.title }}
          </div>
          <label class="flex flex-col gap-1 text-xs text-n-slate-11">
            De
            <input
              v-model="filterFrom"
              type="date"
              class="px-2 py-1 text-sm rounded border border-n-weak bg-n-solid-2 text-n-slate-12"
            />
          </label>
          <label class="flex flex-col gap-1 text-xs text-n-slate-11">
            Até
            <input
              v-model="filterTo"
              type="date"
              class="px-2 py-1 text-sm rounded border border-n-weak bg-n-solid-2 text-n-slate-12"
            />
          </label>
          <div class="flex gap-2 justify-end mt-1">
            <button
              v-if="bankOverrides[bank.key]"
              class="px-2 py-1 text-xs text-n-slate-11 hover:text-n-slate-12"
              @click="clearFilter(bank.key)"
            >
              Limpar
            </button>
            <button
              class="px-2 py-1 text-xs text-n-slate-11 hover:text-n-slate-12"
              @click="closeFilter"
            >
              Cancelar
            </button>
            <button
              class="px-2 py-1 text-xs text-white rounded bg-n-blue-10 hover:bg-n-blue-11"
              :disabled="!filterFrom || !filterTo"
              @click="applyFilter(bank.key)"
            >
              Aplicar
            </button>
          </div>
        </div>
      </div>
    </div>

    <h3 class="mt-2 text-sm font-semibold tracking-wider text-n-blue-10">
      TOTAL PRODUÇÃO SEGUE
    </h3>

    <div class="grid grid-cols-1 gap-3 lg:grid-cols-4">
      <div
        class="flex flex-col gap-2 p-4 rounded-2xl border border-n-weak bg-n-background min-h-[280px]"
      >
        <div class="text-xs font-semibold text-n-slate-12">HOJE</div>
        <div class="flex-1 min-h-0">
          <Doughnut
            v-if="pizzaHoje.labels.length"
            :data="pizzaHoje"
            :options="pizzaHojeOptions"
          />
          <p v-else class="text-xs text-center text-n-slate-11">Sem dados</p>
        </div>
        <div class="text-xs text-center text-n-slate-11">
          {{ totalHoje }}
        </div>
      </div>

      <div
        class="flex flex-col gap-2 p-4 rounded-2xl border border-n-weak bg-n-background min-h-[280px]"
      >
        <div class="text-xs font-semibold text-n-slate-12">MÊS</div>
        <div class="flex-1 min-h-0">
          <Doughnut
            v-if="pizzaMes.labels.length"
            :data="pizzaMes"
            :options="pizzaMesOptions"
          />
          <p v-else class="text-xs text-center text-n-slate-11">Sem dados</p>
        </div>
        <div class="text-xs text-center text-n-slate-11">
          {{ totalMes }}
        </div>
      </div>

      <div
        class="flex flex-col gap-2 p-4 rounded-2xl border border-n-weak bg-n-background lg:col-span-2 min-h-[280px]"
      >
        <div class="flex justify-between items-center">
          <div class="text-xs font-semibold text-n-slate-12">Série</div>
          <div class="flex flex-wrap gap-1">
            <button
              v-for="p in [
                { k: 'hoje', label: 'HOJE' },
                { k: '7d', label: '7 DIAS' },
                { k: 'mes', label: 'MÊS' },
                { k: 'ano', label: 'ANO' },
              ]"
              :key="p.k"
              class="px-2 py-0.5 text-xs rounded border tracking-wide"
              :class="
                seriePeriod === p.k
                  ? 'text-n-slate-12 border-n-blue-10 bg-n-blue-3'
                  : 'text-n-slate-11 border-n-weak hover:text-n-slate-12'
              "
              @click="seriePeriod = p.k"
            >
              {{ p.label }}
            </button>
          </div>
        </div>
        <div class="flex-1 min-h-0">
          <Bar :data="serieChart" :options="serieOptions" />
        </div>
      </div>
    </div>
  </section>
</template>
